import PropTypes from "prop-types"
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { Badge, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import ListDataTable from "../../components/Common/list-data-table"
import { AvForm } from "availity-reactstrap-validation"
import { Box, TextField } from "@mui/material"
import {
  apiBuscarIAPrompts,
  apiRemoverIAPrompt,
} from "../../helpers/backend_helper"
import { translationTable } from "./utils/translation"
import { apiBuscarTiposEmpresa } from "../../helpers/backend_helper"
import { Link } from "react-router-dom"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import ptBR from "date-fns/locale/pt-BR"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import "react-datepicker/dist/react-datepicker.css"
import {ptBR as ptBRDatePickers} from '@mui/x-date-pickers'
import {registerLocale, setDefaultLocale} from "react-datepicker"
import {describeDate, describeDateTime, removeHour} from "../../helpers/utils"

const ptBRLocale = ptBRDatePickers.components.MuiLocalizationProvider.defaultProps.localeText

registerLocale("ptBR", ptBR)
setDefaultLocale("ptBR")

const AiPromptsTable = forwardRef((props, refFather) => {
  const ref = useRef()
  const { filters, usePermissionRemove, usePermissionEdit, navegarEditarPrompt, removerPrompt } = props

  const [data, setData] = useState([])
  const [modalRemove, setModalRemove] = useState(false)
  const [itemSelecionado, setItemSelecionado] = useState()
  const translate = translationTable(props)
  const [tipoEmpresaFilterOptions, setTipoEmpresaFilterOptions] = useState([])

  React.useEffect(() => {
    const carregarTipos = async () => {
      try {
        const response = await apiBuscarTiposEmpresa()
        if (Array.isArray(response)) {
          setTipoEmpresaFilterOptions(
            response
              .map(t => {
                return {text: t?.descricao, value: t?.id }
              })
              .filter(Boolean)
          )
        }
      } catch (e) {
        console.error(e)
      }
    }
    carregarTipos()
  }, [])

  useImperativeHandle(refFather, () => ({
    search() {
      ref?.current?.search()
    },
  }))

  const showConfirmDelete = item => {
    setItemSelecionado(item)
    setModalRemove(true)
  }

  const remover = () => {
    removerPrompt(itemSelecionado)
    setModalRemove(false)
  }

  const colunas = useMemo(
    () => [
      {
        header: translate.id,
        accessorKey: "id",
        size: 60,
        enableHiding: false,
        muiTableBodyCellProps: { align: "right" },
      },
      {
        header: translate.name,
        accessorKey: "nome",
        size: 200,
        filterFn: "contains",
      },
      {
        header: translate.instructions,
        accessorKey: "instrucoes",
        size: 300,
        filterFn: "contains",
      },
      {
        header: translate.companyType,
        accessorKey: "tipoEmpresa_descricao",
        size: 160,
        filterFn: "equals",
        filterVariant: "select",
        filterSelectOptions: tipoEmpresaFilterOptions,
      },
      {
        header: translate.company,
        accessorKey: "empresa_RazaoSocial",
        size: 200,
        filterFn: "contains",
      },
      {
        header: translate.alteration,
        accessorKey: "altData",
        size: 150,
        Cell: ({ cell }) => describeDateTime(cell?.getValue()),
        sortingFn: "datetime",
        Filter: ({ column }) => (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR} localeText={ptBRLocale}>
            <DatePicker
              onChange={(newValue) => {
                if (newValue) {
                  newValue = removeHour(newValue, false)
                }
                column.setFilterValue(newValue)
              }}
              inputFormat="dd/MM/yyyy"
              componentsProps={{
                actionBar: {
                  actions: ["accept", "clear"]
                }
              }}
              value={column.getFilterValue() ? new Date(column.getFilterValue()) : null}
              views={["year", "month", "day"]}
              renderInput={(props) => <TextField {...props} variant="standard" />}
            />
          </LocalizationProvider>
        )
      },
      {
        header: translate.status,
        accessorKey: "status",
        size: 80,
        filterSelectOptions: [
          {text: "Ativo", value: "A"},
          {text: "Inativo", value: "I"},
        ],
        filterVariant: 'select',
        Cell: ({ cell }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <Badge
              className={
                "font-size-12 badge-soft-" +
                (cell.getValue() === "A" ? "success" : "dark")
              }
              color={cell.getValue() === "A" ? "success" : "dark"}
              pill
            >
              {cell.getValue() === "A" ? translate.active : translate.inactive}
            </Badge>
          </Box>
        ),
      },
    ],
    [translate, tipoEmpresaFilterOptions]
  )

  const renderRowActions = (row, table) => {
    const item = row?.original
    if (!item) return null

    return (<Box sx={{display: 'flex', gap: '1rem'}}>
      {usePermissionEdit && <Link to="#" className="text-primary">
        <i className="mdi mdi-pencil font-size-18 ms-1" id="edittooltip"
           onClick={e => {
             e.preventDefault()
             navegarEditarPrompt(item)
           }}
           title={translate.edit}
        />
      </Link>}
      {usePermissionRemove && <Link to="#" className="text-danger">
        <i className="mdi mdi-delete font-size-18" id="btnDelete"
           onClick={e => {
             e.preventDefault()
             showConfirmDelete(item)
           }} title={translate.delete}/>
      </Link>}
    </Box>)
  }

  return (
    <>
      <Row>
        <Col>
          <AvForm>
            <ListDataTable
              ref={ref}
              data={data}
              setData={setData}
              columns={colunas}
              searchFn={params => apiBuscarIAPrompts(params)}
              enableRowActions
              renderRowActions={(row, table) => renderRowActions(row, table)}
              advancedFilters={filters}
            />
          </AvForm>
        </Col>
      </Row>

      <Modal isOpen={modalRemove}>
        <ModalHeader tag="h4">Confirmação</ModalHeader>
        <ModalBody>
          <AvForm onValidSubmit={(e, v) => {
            remover()
          }}>
            <Row form>
              <Col className="col-12">
                {translate.modalText}
              </Col>
            </Row>
            <Row style={{paddingTop: 10}}>
              <Col>
                <div className="text-right">
                  <button type="submit" className="btn btn-success save-event">
                    {translate.modalConfirm}
                  </button>
                  <button type="button" className="btn btn-light ms-2"
                          onClick={e => setModalRemove(false)}>
                    {translate.modalCancel}
                  </button>
                </div>
              </Col>
            </Row>
          </AvForm>
        </ModalBody>
      </Modal>
    </>
  )
})

AiPromptsTable.propTypes = {
  filters: PropTypes.any,
  usePermissionRemove: PropTypes.any,
  usePermissionEdit: PropTypes.any,
  navegarEditarPrompt: PropTypes.func,
  removerPrompt: PropTypes.func,
  t: PropTypes.func,
}

export default AiPromptsTable
